import React from "react"
import { useTranslation } from "react-i18next"
import { useAlert, useModal, useBikeStore } from "hooks"
import FormElement from "components/Form/FormElement"
import { CertifiedIcon, CalendarIcon } from "components/Elements/Icons"
import * as BikeApi from "features/bike/api/BikeApi"
import dateService from "services/date"

const defaultFormFields = {
  title: "",
  date: new Date(),
  comment: "",
}

const defaultFormEvents = {
  ready: true,
}

export const FormBikeEvent = () => {
  const {
    currentBike: bike,
    fetchBike
  } = useBikeStore()

  const { showModal } = useModal()
  const { t } = useTranslation()
  const { alert } = useAlert()

  const [formFields, setFormFields] = React.useState(defaultFormFields)
  const [formEvents, setFormEvents] = React.useState(defaultFormEvents)

  const [errors, setErrors] = React.useState([])
  const [showLocalErrors, setShowLocalErrors] = React.useState(false)

  

  const handleInputChange = (event) => {
    const target = event.target
    const value = target.type === "checkbox" ? target.checked : target.value
    const name = target.name

    setFormFields({
      ...formFields,
      [name]: value,
    })
  }

  const handleValidation = React.useCallback((name, errs) => {
    setErrors((s) => {
      const cleanErrors = [...s.filter((e) => e.origin !== name)]
      return [...cleanErrors, ...errs]
    })
  }, [])

  const handleSubmit = async (event) => {
    event.preventDefault()
    // Validate inputs
    const formReady = !(errors.length > 0)

    if (!formReady) {
      setShowLocalErrors(true)
    } else {
      // hide error
      setShowLocalErrors(false)

      showModal(
        ModalConfirmation,
        {
          bike: bike,
          event: formFields,
          addEvent: async () => {
            try {
                const { getError } = await BikeApi.addBikeEvent(bike.code, formFields.title, formFields.comment, dateService.formatDate(formFields.date, "yyyy-MM-dd HH:mm:ss"), bike.row_version)
                if (getError()) throw getError()
    
                alert("success", t("event.toast_success", { ns: "bike" }))
                fetchBike(bike.code)
              } catch (error) {
                const erroMsg = error?.message?.toString()
                if (erroMsg) alert("error", erroMsg)
            }
          },
        },
        { style: { maxWidth: "850px" } }
      )

      // prevent form to be submitted again
      setFormEvents({
        ...formEvents,
        ready: false,
      })
    }
  }

  const formElementCommonProps = {
    showErrors: showLocalErrors,
    onValidate: handleValidation,
    onChange: handleInputChange
}

  return (
    <>
      <form className={"c-card p-5 md:p-12 mb-5"}>
        <section className="mb-5">
          <h2 className="c-h2 font-semibold mb-5 uppercase flex items-center">
            {t("event.title", { ns: "bike" })} <span className="ml-2"><CertifiedIcon size={24} color={"var(--successColor)"}/></span>
          </h2>
          <div className="l-grid u-mg-negative-hz-m">
            <div className="l-col-12 l-col-5@main p-5">
              <div className="mb-12">
                {t("event.description", { ns: "bike" })}
              </div>
              <FormElement
                value={formFields.title}
                name="title"
                type="text"
                label={t("event.event_title", { ns: "bike" })}
                className="mb-7"
                required={true}
                {...formElementCommonProps}
              />
              <FormElement
                value={formFields.date}
                name="date"
                type="date"
                label={t("event.event_date", { ns: "bike" })}
                className="mb-7"
                required={true}
                max={new Date().toString()}
                {...formElementCommonProps}
              />
              <FormElement
                value={formFields.comment}
                name="comment"
                type="textarea"
                label={t("event.event_comment", { ns: "bike" })}
                className="mb-7"
                required={false}
                {...formElementCommonProps}
                maxLength={500}
              />
              <button
                type="submit"
                className="c-btn c-btn--primary uppercase c-btn--m px-7"
                onClick={handleSubmit}
                disabled={errors.length > 0}
              >
                {t("event.add", { ns: "bike" })}
              </button>
            </div>
            <div className="l-col-12 l-col-1@main p-5">
              <div
                className="u-hide u-block@main"
                style={{
                  border: "1px solid var(--grey50)",
                  height: "400px",
                  width: "1px",
                  marginLeft: "40%",
                }}
              ></div>
            </div>
            
            {bike.lastEvent && (<div className="lg:w-1/2 w-full p-5">
              <strong>{t("event.last_event", { ns: "bike" })}</strong>
              <p className="mb-6">
                {t("event.last_event_description", { ns: "bike" })}
              </p>
              <div className="flex">
                <div className="block">
                  <div
                    className="u-br-base"
                    style={{
                      backgroundColor: "var(--primaryColor)",
                      width: "43px",
                      padding: "5px",
                    }}
                  >
                    <CalendarIcon size={32} color="white" />
                  </div>
                  <div className="h-full"
                    style={{
                      border: "1px solid var(--primaryColor)",
                      width: "1px",
                      marginLeft: "22px",
                    }}
                  ></div>
                </div>
                <div className="ml-5 mt-10">
                  <p className="mb-2 font-bold">{dateService.formatDate(bike.lastEvent.event_date, dateService.getCurrentShortDateFormat())}</p>
                  <p className="mb-2 font-bold flex items-center">
                    {bike.lastEvent.event_author} <span className="ml-1"><CertifiedIcon size={24} color={"var(--successColor)"}/></span>
                  </p>
                  <p className="mb-7 text-black">
                    {bike.lastEvent.event_title}
                  </p>
                  <p>{bike.lastEvent.event_comment}</p>
                </div>
              </div>
            </div>)}
          </div>
        </section>
      </form>
    </>
  )
}

const ModalConfirmation = ({ bike, event, addEvent }) => {
  const { hideModal } = useModal()
  const { t } = useTranslation()

  const validate = (response) => {
    hideModal()
    addEvent(response)
  }

  return (
    <div className="p-12">
      <h2 className="c-h2 text-primary text-xl font-bold text-center my-7">
        {t("event.confirm_title", { ns: "bike" })}
      </h2>
      <p className="mb-12">
        {t("event.confirm_description", { ns: "bike" })}{" "}
        <strong>{t("event.confirm_description2", { ns: "bike" })}</strong>
      </p>

      <div className="mb-12">
        <p>{t("event.confirm_code", { ns: "bike" })}</p>

        <p><strong>{bike?.bikeCode || bike?.code}</strong></p>

      </div>
      <div className="mb-12">
        <p>{t("event.confirm_event", { ns: "bike" })}</p>

        <p><strong>{event.title}</strong></p>

        {event.comment}
      </div>
      <div className="flex justify-between flex-col md:flex-row-reverse mt-7">
        <button
          type="button"
          className="c-btn c-btn--primary px-7 mb-5 lg:m-0 uppercase"
          onClick={validate}
        >
          {t("global.confirm")}
        </button>

        <button
          type="button"
          className="c-btn c-btn--white-bordered px-7 mb-5 lg:m-0 uppercase"
          onClick={hideModal}
        >
          {t("global.cancel")}
        </button>
      </div>
    </div>
  )
}
